import { computed, observable, runInAction } from 'mobx'
import { distinctUntilChanged, switchMap } from 'rxjs'
import type {
  BillingTransactionsFn,
  FetchBillingMethodsFn,
  FetchBillingTransactionsFn,
  FetchCouponByCodeFn,
} from '@/features/billing'
import { type BillingMethod } from '@/features/billing/profile/BillingMethod'
import { type Coupon } from '@/features/billing/coupons/Coupon'
import type { BillingTransaction } from '@/features/billing/transactions/BillingTransaction'
import { tapAction, toStream } from '@/utils/mobx-util'
import { withAbortSignal } from '@/utils/rx-util'
import type { GetCurrentMemberFn } from '@/features/iam'
import type { AlertRetryWrapperFactory } from '@/features/shell'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { type Job } from '@/features/jobs/Job'
import { type JobCollaboratorsViewModel } from '@/features/jobs/details/JobCollaboratorsViewModel'
import { type ApplyCouponAsAdminDialogViewModel } from '@/features/jobs/ApplyCouponAsAdminDialog/ApplyCouponAsAdminDialogViewModel'
import { type RemoveCouponAsAdminDialogViewModel } from '@/features/jobs/RemoveCouponAsAdminDialog/RemoveCouponAsAdminDialogViewModel'
import { type ChargeOrRefundDialogViewModel } from '@/features/jobs/details/components/ChargeOrRefundDialog/ChargeOrRefundDialogViewModel'
import { type AddManualInvoiceDialogViewModel } from '@/features/jobs/details/components/AddManualInvoiceDialog/AddManualInvoiceDialogViewModel'
import { type SplitPayoutDialogViewModel } from '@/features/jobs/details/components/SplitPayoutDialog/SplitPayoutDialogViewModel'

/**
 * A job's coupon, charges, refunds and payout split.
 */
export class JobBillingViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * The applied coupon, `null` until it has loaded.
   */
  @observable accessor coupon: Coupon | null = null

  @observable private accessor _billingMethods: BillingMethod[] = []

  @observable private accessor _chargeTransactions: BillingTransaction[] = []

  @observable private accessor _refundTransactions: BillingTransaction[] = []

  constructor(
    private readonly _job: Job,
    private readonly _collaboratorsVm: JobCollaboratorsViewModel,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _fetchCouponByCode: FetchCouponByCodeFn,
    private readonly _fetchBillingMethods: FetchBillingMethodsFn,
    private readonly _billingTransactions: BillingTransactionsFn,
    private readonly _fetchBillingTransactions: FetchBillingTransactionsFn,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
    public readonly applyCouponAsAdminVm: ApplyCouponAsAdminDialogViewModel,
    public readonly removeCouponAsAdminVm: RemoveCouponAsAdminDialogViewModel,
    public readonly chargeOrRefundDialogVm: ChargeOrRefundDialogViewModel,
    public readonly addManualInvoiceDialogVm: AddManualInvoiceDialogViewModel,
    public readonly splitPayoutDialogVm: SplitPayoutDialogViewModel,
  ) {
    this._lifecycle.manage(applyCouponAsAdminVm)
    this._lifecycle.manage(
      toStream(() => _job.stripeCharges.slice(), true).pipe(
        switchMap((ids) => _billingTransactions(ids)),
        tapAction((transactions) => {
          this._chargeTransactions = transactions
        }),
      ),
    )
    this._lifecycle.manage(
      toStream(() => _job.stripeRefunds.slice(), true).pipe(
        switchMap((ids) => _billingTransactions(ids)),
        tapAction((transactions) => {
          this._refundTransactions = transactions
        }),
      ),
    )
    this._lifecycle.manage(
      toStream(() => _job.couponCodes, true).pipe(
        distinctUntilChanged(),
        switchMap(() =>
          withAbortSignal((signal) =>
            this._createAlertRetryWrapper(
              'Loading the coupon failed',
              signal,
            )(() => this._fetchCoupon(signal))
              // A failed fetch must not end the stream.
              .catch(() => undefined),
          ),
        ),
      ),
    )
    // `fetch` loads the first owner's billing methods; this follows changes.
    this._lifecycle.manage(
      toStream(() => this._ownerKey).pipe(
        switchMap(() =>
          withAbortSignal((signal) =>
            this._createAlertRetryWrapper(
              'Loading billing methods failed',
              signal,
            )(() => this._fetchBillingMethodsForOwner(signal)).catch(
              () => undefined,
            ),
          ),
        ),
      ),
    )
  }

  @computed
  get couponDisplay() {
    if (!this.coupon) {
      return ''
    }

    return `${this.coupon.code}-${this.coupon.discount}`
  }

  /**
   * Determines whether we can view coupon details.
   */
  @computed
  get canViewCoupon() {
    return (
      Boolean(this.coupon) &&
      (this._collaboratorsVm.championClient?.isCurrentUser ||
        this._getCurrentMember(this._job.workspace.id).permissions
          .adminBillingManage)
    )
  }

  /**
   * Determines whether we can add coupons to the job as Admin.
   */
  @computed
  get canAddCouponAsAdmin() {
    return !this.coupon && this._canManageCouponAsAdmin
  }

  /**
   * Determines whether we can remove coupons from the job as Admin.
   */
  @computed
  get canRemoveCouponAsAdmin() {
    return Boolean(this.coupon) && this._canManageCouponAsAdmin
  }

  /**
   * Available billing methods for charging or refunding.
   */
  @computed
  get availableBillingMethods() {
    if (!this._job.isBillingEnabled) {
      return []
    }

    return this._billingMethods
  }

  /**
   * Whether we are able to charge/refund a job.
   */
  @computed
  get canChargeOrRefundJob() {
    return (
      (this.canChargeJob || this._canRefundJob) &&
      this.availableBillingMethods.length > 0
    )
  }

  /**
   * Whether we are able to charge the job.
   */
  @computed
  get canChargeJob() {
    return (
      this.canManageBilling && this._job.isBillingEnabled && !this._job.isDraft
    )
  }

  /**
   * Whether we are able to add invoices manually.
   */
  @computed
  get canAddManualInvoice() {
    return this._permissions.adminJobUpdate
  }

  /**
   * Whether we are able to manage billing.
   */
  @computed
  get canManageBilling() {
    return this._permissions.adminBillingManage
  }

  /**
   * Job's purchase transactions.
   */
  @computed
  get jobPurchaseTransactions() {
    return this._chargeTransactions.filter(
      (x) => x && x.isPurchase && x.receiptUrl,
    )
  }

  /**
   * Job's refund transactions.
   */
  @computed
  get jobRefundTransactions() {
    return this._refundTransactions.filter((x) => x?.isRefund)
  }

  /**
   * Job's total refund amount, calculated by summing up all refund transactions
   * related to the job.
   */
  @computed
  get jobRefundTransactionAmount() {
    return (
      this.jobRefundTransactions.reduce((total, transaction) => {
        return total + transaction.amount
      }, 0) / 100
    )
  }

  /**
   * Whether we are allowed to view the payout split.
   */
  @computed
  get canViewPayoutSplit() {
    return this._permissions.adminJobView
  }

  /**
   * Whether we can split the payout for the job.
   */
  @computed
  get canSplitPayout() {
    return (
      this._job.workspace.config.payouts.enabled &&
      this._permissions.adminSplitPayouts &&
      Boolean(this._job.payoutSplit?.allocations.length)
    )
  }

  @computed
  get payoutSplitLabel() {
    const splitType = this._job.payoutSplit?.situation.type
    if (!this.canViewPayoutSplit) {
      return undefined
    }
    if (splitType === 'Manual') {
      return 'Allocated'
    }
    if (splitType === 'Auto') {
      return 'Automated'
    }
    return undefined
  }

  /**
   * Whether the current user is allowed to view the payment distribution section.
   */
  @computed
  get canViewPaymentDistributionAsAdmin() {
    return this._permissions.adminViewPricing
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Fetches the job's transactions, and its billing methods for members
   * who manage billing.
   */
  async fetch(signal?: AbortSignal) {
    await this._fetchBillingTransactions([
      ...this._job.stripeCharges,
      ...this._job.stripeRefunds,
    ])
    signal?.throwIfAborted()

    await this._fetchBillingMethodsForOwner(signal)
  }

  @computed
  private get _ownerKey() {
    return this._job.ownerTeam
      ? `team:${this._job.ownerTeam.id}`
      : `member:${this._job.owner.workspace.id}:${this._job.owner.user.id}`
  }

  private async _fetchBillingMethodsForOwner(signal?: AbortSignal) {
    if (
      !this._getCurrentMember(this._job.workspace.id).permissions
        .adminBillingManage
    ) {
      return
    }

    const ownerKey = this._ownerKey
    const billingMethods = await this._fetchBillingMethods(
      this._job.ownerTeam ?? this._job.owner,
    )
    // The fetch can't be aborted, so a superseded result is dropped instead.
    if (signal?.aborted || ownerKey !== this._ownerKey) {
      return
    }
    runInAction(() => {
      this._billingMethods = billingMethods
    })
  }

  private async _fetchCoupon(signal: AbortSignal) {
    const coupon = this._job.couponCodes
      ? await this._fetchCouponByCode(
          this._job.couponCodes,
          this._job.workspace.id,
          signal,
        )
      : null
    runInAction(() => {
      this.coupon = coupon
    })
  }

  @computed
  private get _canManageCouponAsAdmin() {
    return (
      !this._job.completed &&
      this._job.workspace.config.billing.couponsEnabled &&
      this._permissions.adminBillingManage
    )
  }

  @computed
  private get _canRefundJob() {
    return this.canManageBilling && this._job.isBillingEnabled
  }

  @computed
  private get _permissions() {
    return this._getCurrentMember(this._job.workspace.id).permissions
  }
}
